/**
 * Edge state interface for tracking edge execution and conditions
 * This is a type-only export - processing logic is handled by the backend
 */
export interface EdgeState {
    active: boolean;
    lastTriggered?: Date;
    executionCount: number;
    condition?: string;
    metadata?: Record<string, unknown>;
}

/**
 * Advanced edge data interface for enhanced edge functionality
 * This is a type-only export - processing logic is handled by the backend
 */
export interface AdvancedEdgeData {
    labels?: {
        text: string;
        position: "start" | "middle" | "end";
        color?: string;
        configurable?: boolean;
    }[];
    state?: EdgeState;
    processor?: string;
    multiTarget?: boolean;
    targets?: string[];
    dataFlow?: {
        dataType: string;
        schema?: Record<string, unknown>;
        sampleData?: unknown;
    };
}

/**
 * Context provided to edge processors
 * This is a type-only export - processing logic is handled by the backend
 */
export interface ProcessorContext {
    sourceNodeId: string;
    targetNodeId: string;
    sourceData?: unknown;
    edgeData?: AdvancedEdgeData;
    globalState?: Record<string, unknown>;
}

/**
 * Result returned by edge processors
 * This is a type-only export - processing logic is handled by the backend
 */
export interface ProcessorResult {
    shouldExecute: boolean;
    outputData?: unknown;
    error?: string;
    metadata?: Record<string, unknown>;
}

/**
 * Edge processor function type
 * This is a type-only export - processing logic is handled by the backend
 */
export type EdgeProcessorFunction = (context: ProcessorContext) => ProcessorResult | Promise<ProcessorResult>;

/**
 * Edge validation result interface
 */
export interface EdgeValidationResult {
    valid: boolean;
    errors: string[];
}

/**
 * Multi-target edge execution result
 */
export interface MultiTargetResult {
    targetId: string;
    result: ProcessorResult;
}

/**
 * Available built-in edge processors
 */
export const BUILT_IN_PROCESSORS = [
    'always',
    'never',
    'conditional',
    'dataTransform'
] as const;

export type BuiltInProcessor = typeof BUILT_IN_PROCESSORS[number];
